/* global Word */
/**
 * Formata no padrão da marca um documento que JÁ existe — por código, sem o
 * modelo. As decisões (o papel de cada parágrafo, as listas, o que vai para a
 * revisão) são puras, em `word/formatPlan.ts`.
 *
 * O que muda é só FORMA: estilo de título, corpo e citação da marca (com o
 * negrito/itálico do texto preservado — ver inlineEmphasis.ts), listas nativas
 * da marca no nível de cada item, tabelas com a fonte e o cabeçalho da marca. O
 * texto não é tocado, nada muda de lugar, figura e quebra de página ficam.
 */
import { planHouseFormatting, sectionsForReview, } from './formatPlan';
import { TABLE_STYLE } from './houseStyle';
import { makeHouseList, readListStates, removeTypedMarkers } from './lists';
import { restyleExisting } from './restyle';
import { coverPrologueEnd, headingLevel, loadParagraphRegions, markPrologueAsCover, } from './tools';
/** Parágrafos por sync ao aplicar estilos — o Office.js não gosta de lote gigante. */
const LOTE = 120;
/** O que o planejamento precisa saber de cada parágrafo do corpo (poucos syncs). */
async function readParaInfo(context) {
    const ps = context.document.body.paragraphs;
    ps.load('items/text,items/style,items/styleBuiltIn,items/alignment,items/tableNestingLevel,items/font/bold');
    await context.sync();
    const items = ps.items;
    const figuras = items.map((p) => {
        const c = p.inlinePictures;
        c.load('items');
        return c;
    });
    await context.sync();
    const regioes = markPrologueAsCover(await loadParagraphRegions(context, items), await coverPrologueEnd(context, items));
    const listas = await readListStates(context, items);
    const info = items.map((p, i) => {
        var _a;
        return ({
            text: p.text || '',
            styleBuiltIn: String(p.styleBuiltIn || ''),
            headingLevel: headingLevel(p.style || '', String(p.styleBuiltIn || '')),
            region: regioes[i],
            inTable: ((_a = p.tableNestingLevel) !== null && _a !== void 0 ? _a : 0) > 0,
            pictures: figuras[i].items.length,
            alignment: String(p.alignment || ''),
            list: listas[i].inList
                ? { id: listas[i].listId, level: listas[i].level, kind: listas[i].rootKind }
                : null,
            allBold: p.font.bold === true,
        });
    });
    return { items, info };
}
/**
 * As seções que ainda pedem julgamento, lidas do documento AGORA. A revisão
 * recalcula antes de cada turno: um turno que apaga a folha de rosto antiga
 * desloca os índices de todas as seções seguintes.
 */
export async function planFormattingReview() {
    return Word.run(async (context) => {
        const { info } = await readParaInfo(context);
        return sectionsForReview(info, planHouseFormatting(info));
    });
}
/**
 * Formata o corpo inteiro e devolve o relatório e as seções para revisão.
 * `onProgress(feito, total)` acompanha os parágrafos (para o painel mostrar).
 */
export async function formatDocumentToHouseStyle(onProgress) {
    return Word.run(async (context) => {
        const { items, info } = await readParaInfo(context);
        const plan = planHouseFormatting(info);
        const review = sectionsForReview(info, plan);
        const report = {
            styled: 0,
            headings: 0,
            lists: plan.groups.length,
            listItems: plan.groups.reduce((n, g) => n + g.indices.length, 0),
            nestedLists: plan.groups.filter((g) => g.levels.some((l) => l > 0)).length,
            tables: 0,
            typedMarkersRemoved: 0,
            emphasisUnresolved: 0,
            skipped: {},
        };
        plan.roles.forEach((r) => {
            var _a;
            if (r.kind === 'skip')
                report.skipped[r.why] = ((_a = report.skipped[r.why]) !== null && _a !== void 0 ? _a : 0) + 1;
        });
        const estilizar = plan.roles
            .map((r, i) => (r.kind === 'style' ? { i, ref: r.ref } : null))
            .filter((x) => !!x);
        const total = estilizar.length + report.listItems;
        let feito = 0;
        onProgress === null || onProgress === void 0 ? void 0 : onProgress(0, total);
        // (1) títulos, corpo e citações, em lotes — com a ênfase do texto guardada.
        for (let k = 0; k < estilizar.length; k += LOTE) {
            const lote = estilizar.slice(k, k + LOTE);
            report.emphasisUnresolved += await restyleExisting(context, lote.map((e) => items[e.i]), lote.map((e) => e.ref));
            report.styled += lote.length;
            report.headings += lote.filter((e) => e.ref.startsWith('heading_')).length;
            feito += lote.length;
            onProgress === null || onProgress === void 0 ? void 0 : onProgress(feito, total);
        }
        // (2) listas: cada lista do documento vira UMA lista da marca, com o nível
        // de cada item. Sem juntar com a vizinha — são as listas que o documento tem.
        for (const g of plan.groups) {
            const paras = g.indices.map((i) => items[i]);
            if (g.typed)
                report.typedMarkersRemoved += await removeTypedMarkers(context, paras);
            await makeHouseList(context, paras, g.kind, g.levels);
            feito += paras.length;
            onProgress === null || onProgress === void 0 ? void 0 : onProgress(feito, total);
        }
        // (3) tabelas do corpo: fonte e cabeçalho da marca.
        report.tables = await formatBodyTables(context);
        return { report, review };
    });
}
/**
 * Fonte e cabeçalho da marca nas tabelas que o documento já tem. Diferente da
 * tabela NOVA (`applyHouseTableStyle`), não troca o estilo dos parágrafos das
 * células — isso apagaria o negrito que o autor pôs numa célula. O cabeçalho
 * só é pintado quando a 1ª linha é mesmo cabeçalho: marcada para repetir, toda
 * em negrito ou sombreada. Tabela de chave/valor não ganha faixa roxa.
 */
async function formatBodyTables(context) {
    const tabelas = context.document.body.tables;
    tabelas.load('items/headerRowCount');
    await context.sync();
    if (!tabelas.items.length)
        return 0;
    const primeiras = tabelas.items.map((t) => {
        const r = t.rows.getFirst();
        r.load('shadingColor,cellCount');
        r.font.load('bold');
        return r;
    });
    await context.sync();
    tabelas.items.forEach((t, k) => {
        var _a, _b;
        t.font.name = TABLE_STYLE.font.name;
        t.font.size = TABLE_STYLE.font.size;
        t.font.color = TABLE_STYLE.font.color;
        try {
            t.getBorder(Word.BorderLocation.all).color = TABLE_STYLE.borderColor;
        }
        catch (_c) {
            /* host sem bordas por API: fica a borda do documento */
        }
        const linha = primeiras[k];
        const sombra = String(linha.shadingColor || '').toUpperCase();
        const temCabecalho = ((_a = t.headerRowCount) !== null && _a !== void 0 ? _a : 0) > 0 ||
            linha.font.bold === true ||
            (sombra !== '' && sombra !== '#FFFFFF' && sombra !== 'NONE');
        if (!temCabecalho)
            return;
        linha.font.color = TABLE_STYLE.headerText;
        linha.font.bold = true;
        for (let c = 0; c < ((_b = linha.cellCount) !== null && _b !== void 0 ? _b : 0); c++) {
            t.getCell(0, c).shadingColor = TABLE_STYLE.headerFill;
        }
    });
    await context.sync();
    return tabelas.items.length;
}
